import { useCallback, useEffect, useRef, useState } from 'react';

import { Text } from '@vimeo/bokeh';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';

import { createDomName } from '../../../../utils/dom/identifying';
import { BokehTooltip } from '../../../bokeh';

interface IChatMessageAuthorProps extends IBaseDomElementProps {
  isCurrentSender: boolean;
  senderName: string;
}

export function ChatMessageAuthor({
  className = createDomName('chat-message-author'),
  senderName,
}: IChatMessageAuthorProps) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

  // Mirror the shared OverflowToolTip pattern: only enable the tooltip when the name is actually
  // ellipsis-truncated. Measuring on mount and again on hover keeps it correct across resizes
  // without a per-row ResizeObserver.
  const compareSize = useCallback((): void => {
    const node: HTMLParagraphElement | null = textRef.current;

    setIsOverflowing((node?.offsetWidth ?? 0) < (node?.scrollWidth ?? 0));
  }, []);

  useEffect(() => {
    compareSize();
  }, [compareSize, senderName]);

  return (
    <BokehTooltip
      placement={'top'}
      isDisabled={!isOverflowing}
      label={senderName}
      shouldWrapChildren={false}
    >
      <Text
        ref={textRef}
        className={className}
        variant={'heading-xs'}
        textOverflow={'ellipsis'}
        whiteSpace={'nowrap'}
        overflow={'hidden'}
        maxWidth={'100%'}
        onMouseEnter={compareSize}
      >
        {senderName}
      </Text>
    </BokehTooltip>
  );
}
